import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRefs } from '@halo/hooks';
import type { SessionStore } from '..';
import { MessagesPosition } from '..';

export interface UseMessagesListOptions {
  attrNames?: {
    msgId?: string;
    cliMsgId?: string;
    highlight?: string;
  };
}

export function useMessagesList(
  sessionStore: SessionStore | undefined,
  options?: UseMessagesListOptions
) {
  const {
    attrNames: { msgId: msgIdName, cliMsgId: cliMsgIdName, highlight: highlightName } = {
      msgId: 'data-chat-msg-id',
      cliMsgId: 'data-chat-cli-msg-id',
      highlight: 'data-chat-highlight',
    },
  } = options || {};

  const wrapperRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLElement>(null);
  const listHeightRef = useRef<number>(0);
  const sessionStoreRef = useRef<SessionStore>(sessionStore);

  const session_id = sessionStore?.session?.chat_id;
  const ready = sessionStore?.ready;
  const changeTrigger = sessionStore?.messagesChangeTrigger;
  const enhancedMessages = sessionStore?.enhancedMessages;
  const highlightDomsRef = useRef<HTMLElement[]>([]);

  const msgReadIO = useMemo(() => {
    const readIds = new Set<string>();
    let isHandling = false;
    return new IntersectionObserver(
      (entries) => {
        if (!sessionStoreRef.current) {
          return;
        }
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const msgId = (entry.target as HTMLElement).getAttribute(msgIdName);
            if (msgId) {
              readIds.add(msgId);
            }
          }
        });

        if (!isHandling) {
          isHandling = true;
          setTimeout(() => {
            if (readIds.size) {
              const { session, readMessages } = sessionStoreRef.current;
              if (session) {
                readMessages(Array.from(readIds));
                readIds.clear();
              }
            }
            isHandling = false;
          }, 1000);
        }
      },
      {
        threshold: 0,
      }
    );
  }, [msgIdName]);

  // Handle messages list height change
  const msgRO = useMemo(() => {
    const domsSet = new Set<Element>();
    const ro = new ResizeObserver((entries) => {
      const sessionStore = sessionStoreRef.current;
      const wrapperDom = wrapperRef.current;
      const isBottom = sessionStore?.position === MessagesPosition.Bottom;
      if (!wrapperDom || !isBottom) {
        return;
      }

      let oldHeight = 0;
      let newHeight = 0;

      entries.forEach((entry) => {
        const { target, contentRect } = entry as any;
        if (target.oldHeight) {
          oldHeight += target.oldHeight;
        }
        newHeight += contentRect.height;
        target.oldHeight = contentRect.height;
      });

      const diffHeight = newHeight - oldHeight;

      if (oldHeight && diffHeight > 0) {
        wrapperDom.scrollBy({ top: diffHeight });
      }
    });

    const wrapperRo = new ResizeObserver(() => {
      const sessionStore = sessionStoreRef.current;
      const wrapperDom = wrapperRef.current;
      const isBottom = sessionStore?.position === MessagesPosition.Bottom;
      if (!isBottom || !wrapperDom) {
        return;
      }
      wrapperDom.scrollBy({ top: 9999 });
    });

    function observe(msgDom: Element) {
      if (domsSet.has(msgDom)) {
        return;
      }
      const oldHeight = msgDom.getBoundingClientRect().height;
      Object.assign(msgDom, { oldHeight });
      domsSet.add(msgDom);
      ro.observe(msgDom);
    }

    function unobserve(msgDom: Element) {
      if (!msgDom.isConnected) {
        domsSet.delete(msgDom);
        ro.unobserve(msgDom);
      }
    }

    return {
      ro,
      wrapperRo,
      observe,
      unobserve,
    };
  }, []);

  sessionStoreRef.current = sessionStore;

  const { ref: topRef, isIntersecting: atTop } = useIntersection({
    getRoot: () => wrapperRef.current,
    options: {
      rootMargin: '100px 0px 0px 0px',
    },
  });

  const { ref: bottomRef, isIntersecting: atBottom } = useIntersection({
    getRoot: () => wrapperRef.current,
    options: {
      rootMargin: '0px 0px 100px 0px',
    },
  });

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [bottomRef]);

  const refs = useRefs({
    session_id,
  });

  // Scroll effect
  useEffect(() => {
    const dom = wrapperRef.current;
    const sessionStore = sessionStoreRef.current;
    if (!dom || !sessionStore) {
      return;
    }

    const handleScroll = () => {
      highlightDomsRef.current.forEach((dom) => {
        dom.removeAttribute(highlightName);
      });
      highlightDomsRef.current = [];
      sessionStore.setScrollCache(dom.scrollTop);
    };

    dom.addEventListener('scroll', handleScroll);

    return () => {
      dom.removeEventListener('scroll', handleScroll);
    };
  }, [session_id, ready, highlightName]);

  // Position effect
  useEffect(() => {
    const { session_id } = refs.current;
    if ([session_id, atTop, atBottom].every((item) => item !== undefined)) {
      sessionStoreRef.current?.setMessagesPosition({
        position:
          atTop && atBottom
            ? MessagesPosition.Both
            : atTop
              ? MessagesPosition.Top
              : atBottom
                ? MessagesPosition.Bottom
                : MessagesPosition.Middle,
      });
    }
  }, [atTop, atBottom, ready, refs]);

  // Do resize
  useEffect(() => {
    const listDom = listRef.current;
    if (!listDom) {
      return;
    }
    const msgDomList = listDom.querySelectorAll(`[${cliMsgIdName}]`);
    msgDomList.forEach(msgRO.observe);
    return () => {
      msgDomList.forEach(msgRO.unobserve);
    };
  }, [enhancedMessages, msgRO, cliMsgIdName, ready]);

  useEffect(() => {
    const wrapperDom = wrapperRef.current;
    if (!wrapperDom) {
      return;
    }
    msgRO.wrapperRo.observe(wrapperDom);
    return () => {
      msgRO.wrapperRo.unobserve(wrapperDom);
    };
  }, [msgRO, ready]);

  // Message read effect
  useEffect(() => {
    const listDom = listRef.current;
    if (!listDom) {
      return;
    }
    const msgDomList = listDom.querySelectorAll(`[${msgIdName}]`);
    msgDomList.forEach((msgDom) => {
      msgReadIO.observe(msgDom);
    });
    return () => {
      msgDomList.forEach((msgDom) => {
        msgReadIO.unobserve(msgDom);
      });
    };
  }, [enhancedMessages, msgReadIO, msgIdName, ready]);

  // Change trigger effect
  useEffect(() => {
    const listDom = listRef.current;
    const wrapperDom = wrapperRef.current;
    if (!listDom || !wrapperDom) {
      return;
    }

    const currentHeight = listDom.clientHeight;

    const diffHeight = currentHeight - listHeightRef.current;
    const requireScroll = changeTrigger?.requireScroll;
    const scrollCache = sessionStoreRef.current?.scrollCache ?? 0;
    const highlight = changeTrigger?.highlight;

    switch (typeof requireScroll) {
      case 'boolean':
        if (requireScroll) {
          wrapperDom.scrollTo({ top: diffHeight + scrollCache });
        }
        break;
      case 'number':
        wrapperDom.scrollTo({ top: requireScroll });
        break;
      case 'string': {
        const scrollDom = listDom.querySelector(`[${cliMsgIdName}="${requireScroll}"]`);
        scrollDom?.scrollIntoView({ block: 'start' });
        break;
      }
    }

    if (highlight) {
      highlight.forEach((cliMsgId) => {
        const dom = listDom.querySelector(`[${cliMsgIdName}="${cliMsgId}"]`) as HTMLElement;
        if (dom) {
          dom.addEventListener(
            'animationend',
            () => {
              highlightDomsRef.current.push(dom);
            },
            { once: true }
          );
          dom.setAttribute(highlightName, 'true');
        }
      });
    }

    listHeightRef.current = currentHeight;
  }, [changeTrigger, cliMsgIdName, highlightName, ready]);

  // To bottom effect
  useEffect(() => {
    if (changeTrigger?.toBottom) {
      scrollToBottom();
    }
  }, [scrollToBottom, changeTrigger?.toBottom, ready]);

  return {
    wrapperRef,
    listRef,
    topRef,
    bottomRef,
    scrollToBottom,
  };
}

export interface UseIntersectionOptions {
  options?: IntersectionObserverInit;
  getRoot?: () => HTMLElement | null;
}

export function useIntersection({
  options: { rootMargin } = {},
  getRoot,
}: UseIntersectionOptions) {
  const ref = useRef<HTMLElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(undefined);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        setIsIntersecting(entries[0].isIntersecting);
      },
      {
        root: getRoot?.(),
        threshold: 0,
        rootMargin,
      }
    );

    if (ref.current) {
      io.observe(ref.current);
    }

    return () => {
      io.disconnect();
    };
  }, [ref, getRoot, rootMargin]);

  return { ref, isIntersecting };
}
